import { useBoolean, useClickAway, useControllableValue } from 'ahooks';
import { Popover } from 'antd';
import React, { FC, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Select } from '../creatable';
import { Option, SingleSelectProps, SingleValue, SelectMode } from '../types';
import { SelectContainer } from './common';
import { renderTriggerPlaceholderOrValue } from './trigger';
import {
  DropdownIndicator,
  getTriggerComponents,
  Menu,
  triggerComponents as TriggerComponents,
} from './trigger-components';

interface MiniSelectProps extends SingleSelectProps {
  triggerComponents?: Partial<typeof TriggerComponents>;
  options?: Option[];
  onChange: (value: SingleValue) => void;
  isClearable?: boolean;
  type?: SelectMode;
}

export const PopoverSelect: FC<MiniSelectProps> = (props) => {
  const {
    options = [],
    isClearable = false,
    triggerComponents,
    type = 'user',
  } = props;
  const [
    visible,
    { toggle: toggleVisibility, setTrue: openPopover, setFalse: closePopover },
  ] = useBoolean(false);

  const [state, setState] = useControllableValue<SingleValue>(props, {
    defaultValue: null,
  });
  const ref1 = useRef(null);
  const ref2 = useRef(null);

  useClickAway(() => {
    if (visible) {
      toggleVisibility();
    }
  }, [ref1, ref2]);

  const handleChange = (value: SingleValue) => {
    let selected = value;

    if (isClearable && value === state) {
      selected = null;
    }
    setState(selected);
    closePopover();
  };

  return (
    <Popover
      placement="bottomLeft"
      visible={visible}
      content={
        <SelectContainer ref={ref1}>
          <Select
            type={type}
            menuIsOpen
            autoFocus
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{
              DropdownIndicator,
              Menu,
              IndicatorSeparator: () => null,
            }}
            placeholder={<FormattedMessage id="placeholder.search" />}
            options={options}
            value={state}
            onChange={handleChange}
            onBlur={closePopover}
          />
        </SelectContainer>
      }
    >
      <div ref={ref2}>
        {renderTriggerPlaceholderOrValue({
          value: state,
          components: getTriggerComponents(triggerComponents),
          onClick: openPopover,
          isMulti: false,
          type,
        })}
      </div>
    </Popover>
  );
};
